<template>
  <div class="app-main-content provision-rule-tab">
    <div class="grid-view">
      <div class="main-content">
        <search-item
          ref="searchItem"
          config-module="25002001"
          :show-simple="false"
          :show-adv="true"
          :expand-adv="true"
          :auto-init="false"
          :custom-search-items="customSearchItems"
          @advSearchItemCallBack="onAdvancedQuery"
          @resetFields="onResetFields"
        >
          <div slot="operations">
            <el-button type="primary" @click="addHandler">新增</el-button>
            <el-button type="primary" @click="editHandler">编辑</el-button>
            <el-button type="primary" @click="toggleStatusHandler">启用/停用</el-button>
            <el-button type="danger" @click="deleteHandler">删除</el-button>
          </div>
        </search-item>
        <div class="section grid-wrapper">
          <cargo-ag-grid
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :row-data="pageRowData"
            style="width: 100%; height: 100%; min-height: 360px;"
            @gridReady="params => { gridApi = params.api }"
          />
        </div>
        <div class="pagination-wrapper">
          <cargo-pagination
            :total="filter.total"
            :page-count="pageCount"
            :page.sync="filter.pageNum"
            :limit.sync="filter.pageSize"
            @pagination="loadData"
          />
        </div>
      </div>
    </div>

    <rule-edit-dialog
      v-if="ruleDialogVisible"
      :visible.sync="ruleDialogVisible"
      :rule-uuid="editingRuleUuid"
      scope="position"
      :group-options="groupList"
      @success="loadData"
    />
  </div>
</template>

<script>
import searchItem from '@/components/Cargo/Common/cargoCombinationSearch'
import RuleEditDialog from '../components/RuleEditDialog.vue'
import { queryRules, updateRuleStatus, deleteRule, queryPersonGroups } from '@/api/provision/provisionRule'
import * as ProvisionConstants from '@/class/cargo/model/parameters/ProvisionConstants'
import {
  applySearchConditions,
  createSelectSearchField,
  createTextSearchField,
  initLocalSearchItem
} from '../utils/provisionRuleSearch'

export default {
  name: 'PositionRuleTab',
  components: { searchItem, RuleEditDialog },
  data() {
    return {
      configurableStations: ProvisionConstants.CONFIGURABLE_STATIONS,
      calcTypeOptions: ProvisionConstants.CALC_TYPE_OPTIONS,
      customSearchItems: [
        createSelectSearchField('岗位', 'stationName', ProvisionConstants.CONFIGURABLE_STATIONS),
        createTextSearchField('规则名称', 'ruleName'),
        createSelectSearchField('计算类型', 'calcType', ProvisionConstants.CALC_TYPE_OPTIONS),
        createSelectSearchField('状态', 'status', [
          { label: '启用', value: '1' },
          { label: '停用', value: '0' }
        ])
      ],
      filter: {
        stationName: '',
        ruleName: '',
        calcType: '',
        status: '',
        pageNum: 1,
        pageSize: 50,
        total: 0
      },
      allRowData: [],
      groupList: [],
      selectData: null,
      selectedRows: [],
      gridApi: null,
      gridOptions: {
        rowSelection: 'multiple',
        suppressCellSelection: true,
        suppressRowClickSelection: true,
        onRowClicked: (event) => {
          this.selectData = event.data
        },
        onSelectionChanged: (event) => {
          this.selectedRows = event.api.getSelectedRows()
          this.selectData = this.selectedRows[0] || null
        }
      },
      columnDefs: [
        {
          headerName: '',
          width: 55,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          resizable: false
        },
        { headerName: '规则名称', field: 'ruleName', width: 160 },
        { headerName: '岗位', field: 'stationName', width: 120 },
        {
          headerName: '类型',
          field: 'calcType',
          width: 110,
          valueFormatter: (params) => ProvisionConstants.getCalcTypeLabel(params.value)
        },
        {
          headerName: '比例/金额',
          width: 140,
          valueFormatter: (params) => ProvisionConstants.formatRuleValue(params.data)
        },
        {
          headerName: '状态',
          field: 'status',
          width: 80,
          cellRenderer: (params) => {
            const on = params.value === '1'
            const color = on ? '#67C23A' : '#909399'
            return `<span style="color:${color}">${on ? '启用' : '停用'}</span>`
          }
        },
        {
          headerName: '生效日期',
          field: 'effectiveDate',
          width: 110,
          valueFormatter: (params) => (params.value ? String(params.value).substring(0, 10) : '')
        },
        {
          headerName: '失效日期',
          field: 'expireDate',
          width: 110,
          valueFormatter: (params) => (params.value ? String(params.value).substring(0, 10) : '')
        }
      ],
      ruleDialogVisible: false,
      editingRuleUuid: ''
    }
  },
  computed: {
    pageRowData() {
      const start = (this.filter.pageNum - 1) * this.filter.pageSize
      return this.allRowData.slice(start, start + this.filter.pageSize)
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filter.total / this.filter.pageSize) || 1)
    }
  },
  methods: {
    init() {
      this.$nextTick(() => initLocalSearchItem(this.$refs.searchItem))
      this.loadGroups()
      this.loadData()
    },
    onAdvancedQuery(conditions) {
      applySearchConditions(this.filter, conditions, ['stationName', 'ruleName', 'calcType', 'status'])
      this.filter.pageNum = 1
      this.loadData()
    },
    onResetFields() {
      applySearchConditions(this.filter, [], ['stationName', 'ruleName', 'calcType', 'status'])
      this.filter.pageNum = 1
      this.loadData()
    },
    async loadGroups() {
      try {
        const res = await queryPersonGroups({})
        if (res.ok) {
          this.groupList = (res.data || []).map(g => ({
            uuid: g.uuid,
            groupName: g.groupName,
            stationName: g.stationName,
            members: g.members || []
          }))
        }
      } catch (e) {
        console.warn('loadGroups failed', e)
      }
    },
    async loadData() {
      try {
        const params = {
          targetType: 'position',
          stationName: this.filter.stationName || undefined,
          ruleName: this.filter.ruleName || undefined,
          calcType: this.filter.calcType || undefined,
          status: this.filter.status || undefined
        }
        const res = await queryRules(params)
        if (!res.ok) {
          this.$message.error('加载岗位规则失败')
          return
        }
        this.allRowData = (res.data || []).filter(r => r.calcType !== '_config')
        this.filter.total = this.allRowData.length
        if (this.filter.pageNum > this.pageCount) {
          this.filter.pageNum = 1
        }
        this.selectData = null
        this.selectedRows = []
      } catch (e) {
        console.error('loadData failed', e)
        this.$message.error('加载岗位规则失败')
      }
    },
    addHandler() {
      this.editingRuleUuid = ''
      this.ruleDialogVisible = true
    },
    getCheckedRows() {
      if (this.gridApi) {
        return this.gridApi.getSelectedRows()
      }
      return this.selectedRows || []
    },
    requireSingleRow() {
      const rows = this.getCheckedRows()
      if (rows.length !== 1) {
        this.$message.warning(rows.length > 1 ? '只能选择一条记录' : '请先选择一条记录')
        return null
      }
      return rows[0]
    },
    editHandler() {
      const row = this.requireSingleRow()
      if (!row) return
      this.editingRuleUuid = row.uuid
      this.ruleDialogVisible = true
    },
    async toggleStatusHandler() {
      const row = this.requireSingleRow()
      if (!row) return
      const next = row.status === '1' ? '0' : '1'
      await updateRuleStatus(row.uuid, next)
      this.$message.success(next === '1' ? '已启用' : '已停用')
      this.loadData()
    },
    deleteHandler() {
      const rows = this.getCheckedRows()
      if (!rows.length) {
        this.$message.warning('请先选择记录')
        return
      }
      this.$confirm(`确认删除选中的 ${rows.length} 条规则？`, '提示', { type: 'warning' }).then(async() => {
        await Promise.all(rows.map(row => deleteRule(row.uuid)))
        this.$message.success('已删除')
        this.loadData()
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.grid-wrapper {
  flex-grow: 1;
  min-height: 360px;
}
.pagination-wrapper {
  margin-top: 8px;
  text-align: right;
}
</style>
